/* ============================================================
   SettleView — per-table tab list with cash / EFTPOS settle.
   Owner ask Feb 2026: tapping an occupied tile lets staff
   close out an individual customer's tab. Cash / EFTPOS chosen
   at this step (not at order-create time).

   Each tab card also shows a live order summary mirroring the
   customer cart: every placed order on this tab, each item with
   its current kitchen/bar status (received / preparing / served).

   Extracted from POSPage.jsx (Feb 2026) — first step of the
   POSPage refactor described in PRD.md → P2 backlog. Behaviour
   and data-testids are unchanged.
   ============================================================ */
import React from "react";
import {
  Banknote,
  CreditCard,
  Plus,
  ArrowLeft,
  ArrowRightLeft,
  UserPlus,
  Users,
  CheckCircle2,
} from "lucide-react";
import { Button } from "../ui/button";

// Tailwind classes for the per-item status badge inside the tab cards.
// Mirrors the BarPanel / Live Orders palette so till + bar agree.
const POS_ITEM_STATUS_STYLES = {
  received: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  preparing: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  ready: "bg-purple-500/15 text-purple-300 border-purple-500/30",
  served: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  completed: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
};
const _itemStatus = (s) => (s || "received").toLowerCase();
const _statusLabel = (s) => {
  const t = _itemStatus(s);
  if (t === "completed") return "served";
  return t;
};

export default function SettleView({
  table,
  tabOrders = {},
  tableLimit = 1000,
  busyId,
  onSettle,
  onSettleAll,
  onBack,
  onAddItems,
  onMove,
  onStartNewTab,
}) {
  const tabs = table.active_tabs || table.tabs || [];
  // Tab unpaid limit — kept aligned with the backend constant
  // `TAB_LIMIT`. Sourced from the tab doc when present (the server
  // includes per-tab overrides on `tab.limit`), otherwise the
  // default $250 the owner standardised on Feb 2026.
  const TAB_LIMIT_DEFAULT = 250;
  // Table-wide unpaid roll-up (Feb 2026 owner ask): the backend
  // blocks any new order on this table once total unpaid across
  // every active tab hits `tableLimit` ($1000 default). We surface
  // it at the top of the settle view so the till sees how much
  // runway the whole table has left.
  const tableUnpaid = tabs.reduce(
    (s, t) => s + Math.max(0, Number(t.current_total || 0) - Number(t.paid_amount || 0)),
    0
  );
  const tablePct = Math.min(100, Math.round((tableUnpaid / tableLimit) * 100));
  const tableNear = tableUnpaid >= tableLimit * 0.8;
  // Aggregate outstanding across all tabs — feeds the master "Settle ALL"
  // strip at the top of the view. Always recomputed from the live `tabs`
  // array so it tracks each per-tab settle the staff makes.
  // Owner ask Feb 2026 (rewrite): Settle ALL nets credits across tabs,
  // so the master strip total must show NET owed (sumOwed - sumCredit).
  // We also surface the sumOwed + sumCredit breakdown when there's any
  // credit to make sure the till and the customer agree on the math.
  const sumOwed = tabs.reduce(
    (s, t) => s + Math.max(0, Number(t.current_total || 0) - Number(t.paid_amount || 0)),
    0
  );
  const sumCredit = tabs.reduce(
    (s, t) => s + Math.max(0, Number(t.paid_amount || 0) - Number(t.current_total || 0)),
    0
  );
  const tableNet = Math.max(0, sumOwed - sumCredit);
  const settlingAll = busyId === "__all__";
  return (
    <div className="flex-1 overflow-y-auto" data-testid="pos-settle-view">
      <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between">
        <div>
          <p className="font-serif text-2xl text-white">Table {table.number}</p>
          <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-sans mt-1">
            {tabs.length} active {tabs.length === 1 ? "tab" : "tabs"}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Owner ask Feb 2026: "till panel should be able to start more
              tabs for same table. Because some times 2 friends order on
              table separately." Always-visible button so staff can open
              a SECOND, THIRD, … tab even when the table is already
              seated. */}
          {onStartNewTab && (
            <button
              type="button"
              onClick={onStartNewTab}
              className="h-9 px-3 inline-flex items-center gap-1.5 rounded-sm border border-gold/40 bg-gold/[0.08] text-gold hover:bg-gold/[0.18] font-sans text-[11px] uppercase tracking-[0.14em] transition-colors"
              data-testid="pos-settle-start-new-tab"
              title="Open a brand-new tab on this table (separate customer)"
            >
              <UserPlus className="w-3.5 h-3.5" />
              + New Tab
            </button>
          )}
          <button
            type="button"
            onClick={onBack}
            className="text-xs font-sans uppercase tracking-[0.14em] text-zinc-400 hover:text-white inline-flex items-center gap-1.5"
            data-testid="pos-settle-back"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to floor
          </button>
        </div>
      </div>

      {tabs.length === 0 ? (
        <div className="text-center py-20">
          <CheckCircle2 className="w-10 h-10 text-emerald-400/60 mx-auto mb-3" />
          <p className="text-zinc-300 font-sans text-sm">All tabs on this table are closed.</p>
          {onStartNewTab && (
            <button
              type="button"
              onClick={onStartNewTab}
              className="mt-5 h-10 px-4 inline-flex items-center gap-2 rounded-sm border border-gold/40 bg-gold/[0.08] text-gold hover:bg-gold/[0.18] font-sans text-xs uppercase tracking-[0.14em]"
              data-testid="pos-settle-empty-start-new-tab"
            >
              <UserPlus className="w-4 h-4" />
              Start a Tab on This Table
            </button>
          )}
        </div>
      ) : (
        <div className="grid gap-3 p-4 max-w-3xl mx-auto">
          {/* Table-wide unpaid ceiling — owner ask Feb 2026: in addition
              to the per-tab $250 cap, the whole table is gated at
              $1000 unpaid. We surface the running sum + progress bar
              at the very top of the settle view so the till sees how
              much runway the table has before ANY tab gets a 402 on
              its next order. */}
          <div
            className={`rounded-md border-2 px-4 py-3 ${
              tablePct >= 100
                ? "border-rose-500/60 bg-rose-500/[0.08]"
                : tableNear
                ? "border-amber-500/50 bg-amber-500/[0.06]"
                : "border-white/10 bg-white/[0.03]"
            }`}
            data-testid="pos-settle-table-ceiling"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] uppercase tracking-[0.16em] text-zinc-400 font-sans">
                Table unpaid · {tabs.length} {tabs.length === 1 ? "tab" : "tabs"}
              </span>
              <span
                className={`font-mono text-lg ${tableNear ? "text-amber-300" : "text-zinc-300"}`}
                data-testid="pos-settle-table-ceiling-value"
              >
                ${tableUnpaid.toFixed(2)} <span className="text-zinc-600 text-xs">/ ${tableLimit.toFixed(0)}</span>
              </span>
            </div>
            <div className="h-2 w-full rounded-full bg-white/[0.05] overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  tablePct >= 100
                    ? "bg-rose-500"
                    : tableNear
                    ? "bg-amber-400"
                    : "bg-emerald-400/70"
                }`}
                style={{ width: `${tablePct}%` }}
                data-testid="pos-settle-table-ceiling-bar"
              />
            </div>
            {tablePct >= 100 && (
              <p className="text-[11px] text-rose-300 font-sans mt-2" data-testid="pos-settle-table-ceiling-blocked">
                Table at ceiling — new orders blocked until a tab is topped up
              </p>
            )}
          </div>

          {/* Master strip — settle every tab on the table with one
              payment. Hidden when there's only ONE tab because the
              per-tab card already covers that case (same buttons). */}
          {tabs.length > 1 && (
            <div
              className="rounded-md border-2 border-gold/40 bg-gold/[0.06] p-4"
              data-testid="pos-settle-all-card"
            >
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-gold font-serif text-lg flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Settle ALL · whole table
                  </p>
                  <p className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 font-sans mt-1">
                    Closes {tabs.length} tabs in one go · same payment method for all
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 font-sans">
                    {sumCredit > 0 ? "Net to pay" : "Total"}
                  </p>
                  <p
                    className="font-mono text-3xl text-gold"
                    data-testid="pos-settle-all-total"
                  >
                    ${tableNet.toFixed(2)}
                  </p>
                  {sumCredit > 0 && (
                    <p className="text-[10px] text-zinc-500 font-mono mt-1" data-testid="pos-settle-all-breakdown">
                      ${sumOwed.toFixed(2)} owed − ${sumCredit.toFixed(2)} credit
                    </p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-4">
                <Button
                  onClick={() => onSettleAll("cash")}
                  disabled={!!busyId}
                  className="h-12 bg-emerald-600 hover:bg-emerald-500 text-white font-sans uppercase tracking-[0.12em] text-xs"
                  data-testid="pos-settle-all-cash"
                >
                  <Banknote className="w-4 h-4 mr-1.5" />
                  {settlingAll ? "Settling…" : `Settle ALL · Cash`}
                </Button>
                <Button
                  onClick={() => onSettleAll("eftpos")}
                  disabled={!!busyId}
                  className="h-12 bg-blue-600 hover:bg-blue-500 text-white font-sans uppercase tracking-[0.12em] text-xs"
                  data-testid="pos-settle-all-eftpos"
                >
                  <CreditCard className="w-4 h-4 mr-1.5" />
                  {settlingAll ? "Settling…" : `Settle ALL · EFTPOS`}
                </Button>
                <Button
                  onClick={() => onSettleAll("split")}
                  disabled={!!busyId}
                  className="col-span-2 h-12 bg-purple-600 hover:bg-purple-500 text-white font-sans uppercase tracking-[0.12em] text-xs"
                  data-testid="pos-settle-all-split"
                  title="Split — pay part cash, part EFTPOS · 10% off cash portion optional"
                >
                  <Banknote className="w-4 h-4 mr-1" />
                  <CreditCard className="w-4 h-4 mr-1.5" />
                  {settlingAll ? "Settling…" : `Settle ALL · Split (Cash + EFTPOS)`}
                </Button>
              </div>
            </div>
          )}

          {/* Per-tab cards — staff can still close a single customer's
              tab independently (the common case when one person leaves
              early). */}
          {tabs.map((tab) => {
            const owed = Math.max(
              0,
              Number(tab.current_total || 0) - Number(tab.paid_amount || 0)
            );
            const busy = busyId === tab.id;
            const orders = tabOrders[tab.id] || [];
            const limit = Number(tab.limit) || TAB_LIMIT_DEFAULT;
            const ceilingPct = Math.min(100, Math.round((owed / limit) * 100));
            const nearCeiling = owed >= limit * 0.8; // soft warning at 80%
            // Credit = paid - current_total when positive. Surfaces the
            // "$X on the tab" state after a keep-change top-up.
            const credit = Math.max(0, Number(tab.paid_amount || 0) - Number(tab.current_total || 0));
            return (
              <div
                key={tab.id}
                className="rounded-md border border-white/10 bg-zinc-900/60 p-4"
                data-testid={`pos-settle-tab-${tab.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-white font-serif text-lg" data-testid={`pos-settle-tab-${tab.id}-name`}>
                      {tab.customer_name || "Guest"}
                    </p>
                    <p className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 font-sans mt-0.5">
                      Tab · opened {tab.opened_at ? new Date(tab.opened_at).toLocaleTimeString() : ""}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 font-sans">
                      Outstanding
                    </p>
                    <p
                      className="font-mono text-2xl text-gold"
                      data-testid={`pos-settle-tab-${tab.id}-owed`}
                    >
                      ${owed.toFixed(2)}
                    </p>
                  </div>
                </div>

                {/* Total / Paid / Outstanding breakdown — owner ask Feb 2026:
                    when a customer has already part-paid (short pay, top up,
                    deposit, split topup, etc.) staff needs to see at a
                    glance the total bill, how much was already taken,
                    and how much is still owed. Only rendered when there's
                    something to show (paid_amount > 0 OR a credit). */}
                {(Number(tab.paid_amount || 0) > 0.005 || credit > 0.005) && (
                  <div
                    className="mt-3 grid grid-cols-3 gap-2"
                    data-testid={`pos-settle-tab-${tab.id}-breakdown`}
                  >
                    <div className="rounded-sm border border-white/10 bg-black/30 px-3 py-2">
                      <p className="text-[9px] uppercase tracking-[0.16em] text-zinc-500 font-sans">Total bill</p>
                      <p className="font-mono text-base text-zinc-100" data-testid={`pos-settle-tab-${tab.id}-total`}>
                        ${Number(tab.current_total || 0).toFixed(2)}
                      </p>
                    </div>
                    <div className="rounded-sm border border-emerald-500/30 bg-emerald-500/[0.06] px-3 py-2">
                      <p className="text-[9px] uppercase tracking-[0.16em] text-emerald-300 font-sans">Paid so far</p>
                      <p className="font-mono text-base text-emerald-300" data-testid={`pos-settle-tab-${tab.id}-paid`}>
                        ${Number(tab.paid_amount || 0).toFixed(2)}
                      </p>
                    </div>
                    <div className={`rounded-sm border px-3 py-2 ${
                      credit > 0.005
                        ? "border-sky-500/30 bg-sky-500/[0.06]"
                        : "border-gold/30 bg-gold/[0.06]"
                    }`}>
                      <p className={`text-[9px] uppercase tracking-[0.16em] font-sans ${
                        credit > 0.005 ? "text-sky-300" : "text-gold"
                      }`}>
                        {credit > 0.005 ? "Credit" : "Outstanding"}
                      </p>
                      <p className={`font-mono text-base ${
                        credit > 0.005 ? "text-sky-300" : "text-gold"
                      }`}>
                        ${(credit > 0.005 ? credit : owed).toFixed(2)}
                      </p>
                    </div>
                  </div>
                )}

                {/* Live order roll-up — every placed order on this tab,
                    each item with its current kitchen/bar status. Mirrors
                    what the customer sees in their cart so staff and
                    customer share the same source of truth at the till. */}
                {orders.length > 0 && (
                  <div
                    className="mt-3 border-t border-white/5 pt-3 space-y-2"
                    data-testid={`pos-settle-tab-${tab.id}-orders`}
                  >
                    {orders.map((order) => {
                      const items = order.items || [];
                      return (
                        <div
                          key={order.id}
                          className="rounded-sm border border-white/[0.06] bg-black/30 px-3 py-2"
                          data-testid={`pos-settle-tab-${tab.id}-order-${order.id}`}
                        >
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">
                              #{order.id.slice(0, 6).toUpperCase()}
                              <span className="ml-2 text-zinc-600 normal-case tracking-normal">
                                {order.created_at ? new Date(order.created_at).toLocaleTimeString() : ""}
                              </span>
                            </span>
                            <span className="font-mono text-xs text-zinc-300">
                              ${Number(order.total || 0).toFixed(2)}
                            </span>
                          </div>
                          <ul className="space-y-1">
                            {items.map((it, idx) => {
                              const stat = _itemStatus(it.status);
                              const label = _statusLabel(it.status);
                              const cls = POS_ITEM_STATUS_STYLES[stat] || POS_ITEM_STATUS_STYLES.received;
                              return (
                                <li
                                  key={`${order.id}-${idx}`}
                                  className="flex items-center justify-between gap-2 text-xs"
                                >
                                  <span className="text-zinc-300 font-sans truncate">
                                    <span className="text-zinc-500 mr-1">{it.quantity}×</span>
                                    {it.name}
                                  </span>
                                  <span
                                    className={`shrink-0 px-1.5 py-0.5 rounded-sm text-[10px] uppercase tracking-[0.12em] font-sans border ${cls}`}
                                    data-testid={`pos-settle-tab-${tab.id}-item-status-${idx}`}
                                  >
                                    {label}
                                  </span>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Ceiling progress bar + Top Up row — owner ask Feb
                    2026. Visualises the $250 unpaid cap so staff know
                    when to nudge a top-up before the customer hits a
                    402 on their next order. Top-up is available at any
                    time (custom amount + cash/EFTPOS); a $250 quick
                    chip lets staff one-tap when the customer is right
                    at the ceiling. */}
                <div className="mt-3 border-t border-white/5 pt-3 space-y-2.5" data-testid={`pos-settle-tab-${tab.id}-ceiling`}>
                  <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.14em] font-sans">
                    <span className="text-zinc-500">Tab limit</span>
                    <span className={`font-mono ${nearCeiling ? "text-amber-300" : "text-zinc-400"}`}>
                      ${owed.toFixed(2)} <span className="text-zinc-600">/ ${limit.toFixed(0)}</span>
                    </span>
                  </div>
                  <div className="h-1.5 w-full rounded-full bg-white/[0.05] overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${
                        ceilingPct >= 100
                          ? "bg-rose-500"
                          : nearCeiling
                          ? "bg-amber-400"
                          : "bg-emerald-400/70"
                      }`}
                      style={{ width: `${ceilingPct}%` }}
                      data-testid={`pos-settle-tab-${tab.id}-ceiling-bar`}
                    />
                  </div>
                  {credit > 0 && (
                    <p className="text-[11px] text-sky-300 font-sans" data-testid={`pos-settle-tab-${tab.id}-credit`}>
                      <span className="font-mono">${credit.toFixed(2)}</span> credit on tab
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-2 mt-4">
                  <Button
                    onClick={() => onAddItems && onAddItems(tab)}
                    disabled={busy}
                    className="col-span-2 h-11 bg-gold/15 hover:bg-gold/25 border border-gold/40 text-gold font-sans uppercase tracking-[0.12em] text-xs"
                    data-testid={`pos-add-items-${tab.id}`}
                    title="Add more drinks/food/shisha to this tab"
                  >
                    <Plus className="w-4 h-4 mr-1.5" />
                    Add Items to {tab.customer_name || "Tab"}
                  </Button>
                  <Button
                    onClick={() => onSettle(tab, "cash")}
                    disabled={busy || (owed <= 0 && credit <= 0)}
                    className={`h-11 ${credit > 0 && owed <= 0
                      ? "bg-sky-600 hover:bg-sky-500"
                      : "bg-emerald-600 hover:bg-emerald-500"} text-white font-sans uppercase tracking-[0.12em] text-xs`}
                    data-testid={`pos-settle-tab-${tab.id}-cash`}
                  >
                    <Banknote className="w-4 h-4 mr-1.5" />
                    {busy
                      ? "Settling…"
                      : credit > 0 && owed <= 0
                        ? `Refund $${credit.toFixed(2)} · Cash`
                        : "Settle Cash"}
                  </Button>
                  <Button
                    onClick={() => onSettle(tab, "eftpos")}
                    disabled={busy || (owed <= 0 && credit <= 0)}
                    className={`h-11 ${credit > 0 && owed <= 0
                      ? "bg-sky-600 hover:bg-sky-500"
                      : "bg-blue-600 hover:bg-blue-500"} text-white font-sans uppercase tracking-[0.12em] text-xs`}
                    data-testid={`pos-settle-tab-${tab.id}-eftpos`}
                  >
                    <CreditCard className="w-4 h-4 mr-1.5" />
                    {busy
                      ? "Settling…"
                      : credit > 0 && owed <= 0
                        ? `Refund $${credit.toFixed(2)} · EFTPOS`
                        : "Settle EFTPOS"}
                  </Button>
                  {/* Owner ask Feb 2026: split (cash + eftpos at once)
                      with 10% off cash portion. Disabled when there's
                      nothing to settle. */}
                  <Button
                    onClick={() => onSettle(tab, "split")}
                    disabled={busy || owed <= 0}
                    className="col-span-2 h-11 bg-purple-600 hover:bg-purple-500 text-white font-sans uppercase tracking-[0.12em] text-xs"
                    data-testid={`pos-settle-tab-${tab.id}-split`}
                    title="Split — pay part cash, part EFTPOS"
                  >
                    <Banknote className="w-4 h-4 mr-1" />
                    <CreditCard className="w-4 h-4 mr-1.5" />
                    Split · Cash + EFTPOS
                  </Button>
                  {/* Owner ask Feb 2026: Move Table — customer switched
                      seats mid-service. Relocates tab + every live
                      order to a different table in one shot. */}
                  {onMove && (
                    <Button
                      onClick={() => onMove(tab)}
                      disabled={busy}
                      className="col-span-2 h-10 bg-white/[0.03] hover:bg-white/[0.07] border border-white/10 text-zinc-300 font-sans uppercase tracking-[0.12em] text-[11px]"
                      data-testid={`pos-move-tab-${tab.id}`}
                      title="Move this tab + all its orders to another table"
                    >
                      <ArrowRightLeft className="w-3.5 h-3.5 mr-1.5" />
                      Move to Another Table
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
